import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Empty, Input, Space, App } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import {
  CopyOutlined,
  PlusOutlined,
  ReadOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/PageHeader";
import { askDataQuestion } from "../../services/data-questions/askStream";
import {
  useGetDataQuestionThreadQuery,
  useGetDataQuestionsCatalogQuery,
  useGetDataQuestionsStatusQuery,
  useListDataQuestionThreadsQuery,
  useListSavedDataQuestionsQuery,
  useRerunDataQuestionMutation,
  useSaveDataQuestionMutation,
  useSetDataQuestionFeedbackMutation,
  useUnsaveDataQuestionMutation,
} from "../../store/api/dataQuestionsApi";
import type {
  DataQuestionFeedback,
  DataQuestionStep,
  DataQuestionTurn,
} from "../../store/api/dataQuestionsApi";
import { getApiErrorMessage } from "../../utils";
import { CatalogDrawer } from "./CatalogDrawer";
import { ThreadsSidebar } from "./ThreadsSidebar";
import { TraceSteps } from "./TraceSteps";
import { TurnCard } from "./TurnCard";
import styles from "./styles.module.css";

const DISABLED_MESSAGE =
  "Las consultas de datos no están habilitadas en este entorno: falta READONLY_DATABASE_URL en el backend.";

/** `/consultas/:threadId` must be a positive integer; anything else is no thread. */
const parseThreadId = (raw: string | undefined): number | undefined => {
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : undefined;
};

/**
 * Server turns win for the ids they know; turns streamed in this session
 * that the (possibly stale) server snapshot does not have yet are kept.
 */
const mergeTurns = (
  server: DataQuestionTurn[],
  local: DataQuestionTurn[]
): DataQuestionTurn[] => {
  const known = new Set(server.map((turn) => turn.turnId));
  return [...server, ...local.filter((turn) => !known.has(turn.turnId))].sort(
    (a, b) => a.turnId - b.turnId
  );
};

/**
 * Natural-language questions over the read-only data views. One thread per
 * URL (/consultas/:threadId) so a conversation can be shared by link; a new
 * question without a thread creates one and navigates to it.
 */
export default function DataQuestions() {
  const { message } = App.useApp();
  const params = useParams<{ threadId?: string }>();
  const threadId = parseThreadId(params.threadId);
  const invalidThreadParam = params.threadId !== undefined && threadId === undefined;
  const navigate = useNavigate();

  const { data: status } = useGetDataQuestionsStatusQuery();
  const { data: catalog } = useGetDataQuestionsCatalogQuery();
  const { data: threads, refetch: refetchThreads } = useListDataQuestionThreadsQuery();
  const { data: saved } = useListSavedDataQuestionsQuery();
  const {
    // currentData (not data): never the previous thread's turns while the
    // new one loads or fails.
    currentData: thread,
    isFetching: threadLoading,
    error: threadError,
    refetch: refetchThread,
  } = useGetDataQuestionThreadQuery(threadId ?? 0, { skip: !threadId });
  const [setFeedback] = useSetDataQuestionFeedbackMutation();
  const [saveQuestion] = useSaveDataQuestionMutation();
  const [unsaveQuestion] = useUnsaveDataQuestionMutation();
  const [rerunQuestion] = useRerunDataQuestionMutation();

  const [turns, setTurns] = useState<DataQuestionTurn[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<DataQuestionStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rerunningId, setRerunningId] = useState<number | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /** Thread this page just created: its turns are already on screen. */
  const createdThreadRef = useRef<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextAreaRef>(null);

  // Switching threads: drop the previous transcript and stop any stream that
  // would otherwise land its answer in the wrong conversation.
  useEffect(() => {
    if (createdThreadRef.current === threadId) {
      createdThreadRef.current = null;
      return;
    }
    abortRef.current?.abort();
    setTurns([]);
    setError(null);
  }, [threadId]);

  // Fresh server turns for the open thread; locally streamed ones are kept.
  useEffect(() => {
    if (!threadId || !thread || thread.threadId !== threadId) return;
    setTurns((current) => mergeTurns(thread.turns, current));
  }, [threadId, thread]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [turns.length, liveSteps.length]);

  const enabled = status?.enabled !== false;
  const links = catalog?.links ?? {};

  const submit = useCallback(
    async (raw: string) => {
      const question = raw.trim();
      if (!question || pending || !enabled) return;
      setError(null);
      setInput("");
      setPending(question);
      setLiveSteps([]);
      const controller = new AbortController();
      abortRef.current = controller;
      const created = { threadId: null as number | null };
      try {
        await askDataQuestion(
          { question, threadId },
          (event) => {
            if (controller.signal.aborted) return;
            if (event.type === "thread") {
              if (!threadId) created.threadId = event.threadId;
            } else if (event.type === "step") {
              setLiveSteps((steps) => [...steps, event.step]);
            } else if (event.type === "result") {
              setTurns((current) => [...current, event.result]);
            } else if (event.type === "error") {
              setError(event.message);
            }
          },
          controller.signal
        );
        if (created.threadId !== null) {
          createdThreadRef.current = created.threadId;
          navigate(`/consultas/${created.threadId}`, { replace: true });
        } else if (threadId) {
          void refetchThread();
        }
        void refetchThreads();
      } catch (requestError) {
        if (!controller.signal.aborted) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : "No se pudo enviar la pregunta."
          );
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setPending(null);
        setLiveSteps([]);
        inputRef.current?.focus();
      }
    },
    [pending, enabled, threadId, navigate, refetchThreads, refetchThread]
  );

  const cancel = () => {
    abortRef.current?.abort();
    setError("Consulta cancelada.");
  };

  const patchTurn = (turnId: number, patch: Partial<DataQuestionTurn>) =>
    setTurns((current) =>
      current.map((turn) => (turn.turnId === turnId ? { ...turn, ...patch } : turn))
    );

  const handleFeedback = async (turnId: number, feedback: DataQuestionFeedback) => {
    try {
      const updated = await setFeedback({ turnId, feedback }).unwrap();
      patchTurn(turnId, { feedback: updated.feedback });
      message.success(feedback === "UP" ? "Marcada como correcta" : "Gracias, lo tenemos en cuenta");
    } catch (requestError) {
      message.error(getApiErrorMessage(requestError, "No se pudo guardar el feedback."));
    }
  };

  const handleSave = async (turnId: number, name: string) => {
    try {
      const updated = await saveQuestion({ turnId, name }).unwrap();
      patchTurn(turnId, { savedName: updated.savedName });
      message.success("Consulta guardada");
    } catch (requestError) {
      message.error(getApiErrorMessage(requestError, "No se pudo guardar la consulta."));
      throw requestError;
    }
  };

  const handleUnsave = async (turnId: number) => {
    try {
      await unsaveQuestion(turnId).unwrap();
      patchTurn(turnId, { savedName: null });
    } catch (requestError) {
      message.error(getApiErrorMessage(requestError, "No se pudo quitar la consulta."));
    }
  };

  const handleRerun = async (turnId: number) => {
    setRerunningId(turnId);
    try {
      const result = await rerunQuestion(turnId).unwrap();
      patchTurn(turnId, { result });
    } catch (requestError) {
      message.error(
        getApiErrorMessage(requestError, "No se pudo volver a ejecutar la consulta.")
      );
    } finally {
      setRerunningId(null);
    }
  };

  const copyLink = async () => {
    if (!threadId) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/consultas/${threadId}`);
      message.success("Link copiado");
    } catch {
      message.error("No se pudo copiar el link.");
    }
  };

  const composerDisabled = !enabled || pending !== null;
  const showEmptyState =
    turns.length === 0 && !pending && !threadLoading && !threadError && !invalidThreadParam;
  const suggestions = catalog?.suggestions ?? [];
  const hasThread = threadId !== undefined;

  return (
    <div>
      <PageHeader
        title="Consultas de datos"
        extra={
          <Space wrap>
            <Button icon={<ReadOutlined />} onClick={() => setCatalogOpen(true)}>
              ¿Qué datos puedo preguntar?
            </Button>
            {hasThread && (
              <Button icon={<CopyOutlined />} onClick={() => void copyLink()}>
                Copiar link
              </Button>
            )}
            {(hasThread || invalidThreadParam || turns.length > 0) && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/consultas")}>
                Nueva consulta
              </Button>
            )}
          </Space>
        }
      />

      {!enabled && (
        <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={DISABLED_MESSAGE} />
      )}

      <div className={styles.layout}>
        <aside aria-label="Historial">
          <ThreadsSidebar
            threads={threads ?? []}
            saved={saved ?? []}
            activeThreadId={threadId}
            onOpen={(id) => navigate(`/consultas/${id}`)}
          />
        </aside>

        <Card
          styles={{
            body: { display: "flex", flexDirection: "column", minHeight: "60vh", padding: 0 },
          }}
        >
          <section className={styles.transcript} aria-label="Conversación" aria-live="polite">
            {(threadError || invalidThreadParam) && (
              <Alert
                type="error"
                showIcon
                message="No se pudo abrir ese hilo"
                description={
                  invalidThreadParam
                    ? "El link no es válido."
                    : getApiErrorMessage(threadError, "Puede haber sido borrado.")
                }
              />
            )}

            {showEmptyState && (
              <div className={styles.emptyState}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    suggestions.length > 0
                      ? "Elegí una pregunta o escribí la tuya"
                      : "Escribí tu pregunta"
                  }
                />
                {suggestions.length > 0 && (
                  <div className={styles.chips}>
                    {suggestions.map((suggestion) => (
                      <Button
                        key={suggestion}
                        shape="round"
                        disabled={composerDisabled}
                        onClick={() => void submit(suggestion)}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {turns.map((turn) => (
              <TurnCard
                key={turn.turnId}
                turn={turn}
                links={links}
                disabled={composerDisabled}
                onFollowUp={(question) => void submit(question)}
                onFeedback={(turnId, feedback) => void handleFeedback(turnId, feedback)}
                onSave={handleSave}
                onUnsave={handleUnsave}
                onRerun={handleRerun}
                rerunning={rerunningId === turn.turnId}
              />
            ))}

            {pending && (
              <div className={styles.turn}>
                <div className={styles.question}>{pending}</div>
                <TraceSteps
                  steps={
                    liveSteps.length > 0
                      ? liveSteps
                      : [{ kind: "thinking", text: "Enviando la pregunta", at: Date.now() }]
                  }
                  live
                />
              </div>
            )}

            {error && (
              <Alert
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                message={error}
              />
            )}
            <div ref={bottomRef} />
          </section>

          <form
            className={styles.composer}
            onSubmit={(event) => {
              event.preventDefault();
              void submit(input);
            }}
          >
            <Input.TextArea
              ref={inputRef}
              aria-label="Pregunta"
              placeholder={hasThread ? "Repreguntá sobre este hilo…" : "¿Qué querés saber?"}
              autoSize={{ minRows: 1, maxRows: 6 }}
              value={input}
              disabled={!enabled}
              onChange={(event) => setInput(event.target.value)}
              onPressEnter={(event) => {
                if (event.shiftKey) return;
                event.preventDefault();
                void submit(input);
              }}
            />
            {pending ? (
              <Button icon={<StopOutlined />} onClick={cancel}>
                Cancelar
              </Button>
            ) : (
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                disabled={composerDisabled || !input.trim()}
              >
                Preguntar
              </Button>
            )}
          </form>
        </Card>
      </div>

      <CatalogDrawer open={catalogOpen} catalog={catalog} onClose={() => setCatalogOpen(false)} />
    </div>
  );
}
